import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import InputAdornment from '@material-ui/core/InputAdornment';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import { Link } from '@reach/router';
import clsx from 'clsx';
import React, { Fragment, FunctionComponent, useState } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import {
  Maybe,
  OutageDetailsByCounty,
  ZipCodeInfo,
} from '../../../../__generated__/pge-types';
import TextFieldWrapped from '../../../text-field';
import useContentMgmt from '../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
    },
    searchContainer: {
      marginBottom: theme.spacing(3),
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, auto)',
      justifyContent: 'space-between',
    },
    gridItem: {
      flex: 1,
      minWidth: '130px',
    },
    gridDivider: {
      gridColumn: 'span 5',
      margin: theme.spacing(1.5, 0, 1.5, 0),
    },
    lastUpdate: {
      paddingTop: theme.spacing(3),
    },
    countyName: {
      textTransform: 'capitalize',
    },
    zipCodeHolder: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    zipCodeToNavigate: {
      marginRight: '5px',
    },
  }),
);

interface OutageListProps extends React.HTMLAttributes<HTMLDivElement> {
  outageList: OutageDetailsByCounty[];
  handleZipcodeClick?: (
    countyName: string | null | undefined,
    zipCode?: string | null | undefined,
    zipIndex?: number | null | undefined,
    zipCodes?: Array<ZipCodeInfo> | undefined | null,
  ) => void;
}

const OutageList: FunctionComponent<OutageListProps> = ({
  outageList,
  handleZipcodeClick,
  ...props
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filterZip, setFilterZip] = useState('');

  const { content } = useContentMgmt(
    microcopyGroupIds.OUTAGES_CUSTOMERS_IMPACTED_BY_COUNTY,
  );

  const { content: countyContent } = useContentMgmt(
    microcopyGroupIds.OUTAGES_BY_COUNTY,
  );

  const filterZipItems = (
    outageListItem: Maybe<OutageDetailsByCounty>,
    zipToFilterFor: string,
  ) => {
    const filteredZipItems:
      | Maybe<ZipCodeInfo>[]
      | undefined = outageListItem?.zipCodeInfo?.filter(i => {
      return i?.zipCode?.toString().startsWith(zipToFilterFor);
    });
    if (filteredZipItems === undefined) {
      return [];
    }
    return filteredZipItems;
  };

  const handleCountyZipClick = (
    countyName: string | null | undefined,
    zipCode?: string | null | undefined,
    zipIndex?: number | null | undefined,
    zipCodes?: Array<ZipCodeInfo> | undefined | null,
  ) => {
    return (
      handleZipcodeClick &&
      handleZipcodeClick(countyName, zipCode, zipIndex, zipCodes)
    );
  };

  const customerImpactedfunc = (outageListItem: any) => {
    if (outageListItem.county === 'CLACKAMAS') {
      return (
        content.get('CLACKAMAS_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    }
    if (outageListItem.county === 'WASHINGTON') {
      return (
        content.get('WASHINGTON_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    }
    if (outageListItem.county === 'MULTNOMAH') {
      return (
        content.get('MULTNOMAH_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    }
    if (outageListItem.county === 'MARION') {
      return (
        content.get('MARION_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    }
    if (outageListItem.county === 'YAMHILL') {
      return (
        content.get('YAMHILL_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    }
    if (outageListItem.county === 'COLUMBIA') {
      return (
        content.get('COLUMBIA_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    }
    if (outageListItem.county === 'POLK') {
      return (
        content.get('POLK_COUNTY_CUSTOMERS_IMPACTED') ||
        outageListItem.totalCustomersImpacted
      );
    } else {
      return outageListItem.totalCustomersImpacted;
    }
  };

  const customerOutagefunc = (outageListItem: any) => {
    if (outageListItem.county === 'CLACKAMAS') {
      return (
        countyContent.get('CLACKAMAS_COUNTY_OUTAGES') || outageListItem?.outages
      );
    }
    if (outageListItem.county === 'WASHINGTON') {
      return (
        countyContent.get('WASHINGTON_COUNTY_OUTAGES') ||
        outageListItem?.outages
      );
    }
    if (outageListItem.county === 'MULTNOMAH') {
      return (
        countyContent.get('MULTNOMAH_COUNTY_OUTAGES') || outageListItem?.outages
      );
    }
    if (outageListItem.county === 'MARION') {
      return (
        countyContent.get('MARION_COUNTY_OUTAGES') || outageListItem?.outages
      );
    }
    if (outageListItem.county === 'YAMHILL') {
      return (
        countyContent.get('YAMHILL_COUNTY_OUTAGES') || outageListItem?.outages
      );
    }
    if (outageListItem.county === 'COLUMBIA') {
      return (
        countyContent.get('COLUMBIA_COUNTY_OUTAGES') || outageListItem?.outages
      );
    }
    if (outageListItem.county === 'POLK') {
      return (
        countyContent.get('POLK_COUNTY_OUTAGES') || outageListItem?.outages
      );
    } else {
      return outageListItem?.outages;
    }
  };

  return (
    <div className={classes.root} {...props}>
      <div className={classes.searchContainer}>
        <TextFieldWrapped
          label={t('FILTER_BY_ZIP_CODE')}
          style={{ flex: 1, width: '100%' }}
          name={'zip'}
          value={filterZip}
          onChange={(
            evt: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
          ) => setFilterZip(evt.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon style={{ color: colors.darkGray2 }} />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div className={classes.gridContainer}>
        <Box className={classes.gridItem}>
          <Typography variant={'subtitle2'}>{t('COUNTY')}</Typography>
        </Box>
        <Box className={classes.gridItem}>
          <Typography variant={'subtitle2'}>{t('OUTAGES')}</Typography>
        </Box>
        <Box className={classes.gridItem}>
          <Typography variant={'subtitle2'}>{t('CUSTOMERS')}</Typography>
        </Box>
        <Box className={classes.gridItem}>
          <Typography variant={'subtitle2'}>{t('ZIP_CODES')}</Typography>
        </Box>
        <Divider className={classes.gridDivider} />
        {!outageList
          ? null
          : outageList
              .filter(outageListItem => {
                return (
                  !filterZip ||
                  filterZipItems(outageListItem, filterZip).length > 0
                );
              })
              .map((outageListItem, index) => {
                return (
                  <Fragment key={`list-county-item-${index}`}>
                    <Box className={classes.gridItem}>
                      <Typography variant={'subtitle1'} component={'span'}>
                        <Link
                          to={'#'}
                          onClick={e => {
                            e.preventDefault();
                            handleCountyZipClick(
                              outageListItem?.county,
                              null,
                              null,
                              outageListItem?.zipCodeInfo,
                            );
                          }}
                        >
                          <span className={classes.countyName}>
                            {outageListItem?.county}
                          </span>
                        </Link>
                      </Typography>
                    </Box>
                    <Box className={classes.gridItem}>
                      <Typography variant={'subtitle1'}>
                        {customerOutagefunc(outageListItem)}
                      </Typography>
                    </Box>
                    <Box className={classes.gridItem}>
                      <Typography variant={'subtitle1'}>
                        {customerImpactedfunc(outageListItem)}
                      </Typography>
                    </Box>
                    <Box
                      className={clsx(classes.gridItem, classes.zipCodeHolder)}
                    >
                      {filterZipItems(outageListItem, filterZip).map(
                        (zipItem, zipIndex) => {
                          return (
                            <Typography
                              variant={'subtitle1'}
                              key={`list-county-item-${zipIndex}`}
                              className={classes.zipCodeToNavigate}
                            >
                              <Link
                                to={'#'}
                                onClick={e => {
                                  e.preventDefault();
                                  handleCountyZipClick(
                                    null,
                                    zipItem?.zipCode,
                                    zipIndex,
                                    outageListItem?.zipCodeInfo,
                                  );
                                }}
                              >
                                {zipItem?.zipCode}
                              </Link>
                              {', '}
                            </Typography>
                          );
                        },
                      )}
                    </Box>
                    {outageList && outageList.length - 1 === index ? null : (
                      <Divider className={classes.gridDivider} />
                    )}
                  </Fragment>
                );
              })}
      </div>
    </div>
  );
};

export default OutageList;
